import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { SignalCard } from "@/components/SignalCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DateRangePicker, type DateRange } from "@/components/DateRangePicker";
import { StickySubheader } from "@/components/StickySubheader";

export function Signals() {
  // M27d — half-open [from, to) on the signal's createdAt, server-side. The keyset cursor
  // composes with the window: both are plain filters, so paging inside a range just works.
  const [range, setRange] = useState<DateRange | null>(null);
  const dateParams = range ? { from: range.from.toISOString(), to: range.to.toISOString() } : {};
  const query = useInfiniteQuery({
    queryKey: ["signals", dateParams],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      api.signals({ cursor: pageParam, ...dateParams }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const signals = query.data?.pages.flatMap((page) => page.signals) ?? [];

  return (
    <div className="space-y-3">
      <StickySubheader>
        <h1 className="text-lg font-semibold">Signals</h1>
        <DateRangePicker value={range} onChange={setRange} />
      </StickySubheader>

      {query.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {query.isError && <p className="text-sm text-destructive">Could not load signals.</p>}

      {!query.isLoading && signals.length === 0 && (
        <p className="text-sm text-muted-foreground">No signals yet.</p>
      )}

      <div className="space-y-2">
        {signals.map((signal) => (
          <SignalCard key={signal.id} signal={signal} />
        ))}
      </div>

      {query.hasNextPage && (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
        >
          {query.isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      )}
    </div>
  );
}
